import { useState, useEffect } from 'react';
import axiosInstance from '../../../utils/axios';
import { SERVER } from '../../../hooks/config';
import './PersonalisationMatieres.scss';

const PersonalisationMatieres = () => {
  const [matieres, setMatieres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMatiere, setEditingMatiere] = useState(null);
  const [formData, setFormData] = useState({
    name: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchMatieres();
  }, []);

  const fetchMatieres = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/personalization-matiere');
      setMatieres(response.data);
    } catch (error) {
      console.error('Error fetching matieres:', error);
      setError('Failed to fetch personalization materials');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingMatiere) {
        // Update existing matiere
        await axiosInstance.put(`/personalization-matiere/${editingMatiere.id}`, formData);
        setSuccess('Personalization material updated successfully');
      } else {
        // Create new matiere
        await axiosInstance.post('/personalization-matiere', formData);
        setSuccess('Personalization material created successfully');
      }

      // Reset form and refresh list
      setFormData({ name: '' });
      setEditingMatiere(null);
      setShowAddModal(false);
      fetchMatieres();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving matiere:', error);
      if (error.response?.status === 400) {
        setError(error.response.data.error || 'Validation error');
      } else {
        setError('Failed to save personalization material');
      }
    }
  };

  const handleEdit = (matiere) => {
    setEditingMatiere(matiere);
    setFormData({ name: matiere.name });
    setShowAddModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this personalization material?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/personalization-matiere/${id}`);
      setSuccess('Personalization material deleted successfully');
      fetchMatieres();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting matiere:', error);
      setError('Failed to delete personalization material');
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingMatiere(null);
    setFormData({ name: '' });
    setError('');
  };

  if (loading) {
    return <div className="loading">Loading personalization materials...</div>;
  }

  return (
    <div className="personalisation-matieres">
      <div className="section-header">
        <h2>Personalization Materials</h2>
        <button 
          className="btn-primary"
          onClick={() => setShowAddModal(true)}
        >
          Add New Material
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      <div className="matieres-table">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {matieres.map((matiere) => (
              <tr key={matiere.id}>
                <td>{matiere.id}</td>
                <td>{matiere.name}</td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="btn-edit"
                      onClick={() => handleEdit(matiere)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDelete(matiere.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {matieres.length === 0 && (
          <div className="no-data">
            No personalization materials found.
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingMatiere ? 'Edit Material' : 'Add New Material'}</h3>
              <button onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Material Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter material name"
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="primary">
                  {editingMatiere ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalisationMatieres;